import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, LocateFixed } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatINR } from "../lib/products";
import { api } from "../lib/api";
import { toast } from "sonner";

const OPTION_LABEL = {
  lighting: { no_light: "No light", with_light: "With LED light" },
  build: { assembled: "Pre-assembled", pieces_only: "Pieces only (kit)" },
};

const describeOptions = (options) => {
  if (!options) return null;
  return Object.entries(options)
    .map(([g, c]) => OPTION_LABEL[g]?.[c] || c)
    .join(" · ");
};

const EMPTY_ADDR = {
  name: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
};

export default function CartSheet() {
  const { items, removeItem, updateQty, clear, total, open, setOpen } = useCart();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("cart"); // cart | address
  const [attempted, setAttempted] = useState(false);
  const [locating, setLocating] = useState(false);
  const [addr, setAddr] = useState(() => {
    try {
      const raw = localStorage.getItem("apex_delivery");
      return raw ? { ...EMPTY_ADDR, ...JSON.parse(raw) } : EMPTY_ADDR;
    } catch {
      return EMPTY_ADDR;
    }
  });
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("apex_delivery", JSON.stringify(addr));
  }, [addr]);

  const setField = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location is not supported on this device.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&addressdetails=1`,
            { headers: { Accept: "application/json" } }
          );
          const data = await res.json();
          const a = data.address || {};
          const line = [a.house_number, a.road, a.neighbourhood || a.suburb]
            .filter(Boolean)
            .join(", ");
          setAddr((prev) => ({
            ...prev,
            address: line || prev.address,
            city: a.city || a.town || a.village || a.county || prev.city,
            state: a.state || prev.state,
            pincode: (a.postcode || "").replace(/\D/g, "") || prev.pincode,
          }));
          toast.success("Address filled from your location — please double-check it!");
        } catch {
          toast.error("Couldn't look up your address. Please fill it manually.");
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        toast.error("Location permission denied — please fill the form manually.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const closeSheet = () => {
    setOpen(false);
    setStep("cart");
  };

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const handleCheckout = async () => {
    if (!items.length) return;
    const required = ["name", "phone", "email", "address", "city", "state", "pincode"];
    if (required.some((k) => !addr[k].trim())) {
      setAttempted(true);
      toast.error("Please fill in all delivery details.");
      return;
    }
    if (!/^\d{6}$/.test(addr.pincode.trim())) {
      setAttempted(true);
      toast.error("PIN code must be 6 digits.");
      return;
    }
    setLoading(true);
    try {
      const scriptOk = await loadRazorpay();
      if (!scriptOk) throw new Error("Razorpay script failed to load");
      const payload = {
        items: items.map((i) => ({
          slug: i.slug,
          quantity: i.quantity,
          options: i.options || undefined,
        })),
        origin_url: window.location.origin,
        customer_email: addr.email.trim(),
        shipping: {
          name: addr.name.trim(),
          phone: addr.phone.trim(),
          email: addr.email.trim(),
          address: addr.address.trim(),
          city: addr.city.trim(),
          state: addr.state.trim(),
          pincode: addr.pincode.trim(),
        },
      };
      const { data } = await api.post("/checkout/razorpay/order", payload);
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Apex Designs",
        description: "Brick-built F1 frames",
        order_id: data.order_id,
        theme: { color: "#FF2800" },
        prefill: {
          name: addr.name.trim(),
          email: addr.email.trim(),
          contact: addr.phone.trim(),
        },
        handler: async (resp) => {
          try {
            await api.post("/checkout/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              customer_email: addr.email.trim(),
              customer_name: addr.name.trim(),
              customer_phone: addr.phone.trim(),
            });
            clear();
            closeSheet();
            navigate(`/checkout/success?session_id=${resp.razorpay_order_id}`);
          } catch {
            toast.error(
              "Payment received but verification failed — call +91 98200 78697."
            );
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
      });
      rzp.open();
      setLoading(false);
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[60] ${open ? "" : "pointer-events-none"}`}
      aria-hidden={!open}
      data-testid="cart-sheet"
    >
      <div
        className={`absolute inset-0 bg-black/70 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={() => setOpen(false)}
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full sm:w-[420px] bg-[#0a0a0a] border-l border-white/10 transform transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div>
            <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              // Pit Box
            </p>
            <h2 className="af-display text-2xl font-bold uppercase tracking-tight">
              {step === "address" ? "Delivery Details" : "Your Cart"}
            </h2>
          </div>
          <button
            type="button"
            onClick={closeSheet}
            className="w-9 h-9 inline-flex items-center justify-center border border-white/15 hover:border-white/40 transition-colors"
            data-testid="close-cart-button"
            aria-label="Close cart"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-16">
              <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
                // Empty
              </p>
              <p className="text-zinc-400 text-sm">
                No frames in the pit box yet.
              </p>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/shop");
                }}
                className="mt-6 bg-[#FF2800] text-white font-semibold uppercase tracking-wider py-3 px-6 hover:bg-[#CC2000] transition-colors text-xs"
                data-testid="cart-shop-now-button"
              >
                Shop frames
              </button>
            </div>
          )}

          {step === "address" && items.length > 0 && (
            <div className="space-y-4" data-testid="shipping-address-form">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-zinc-400 text-sm">Where should we ship your order?</p>
                <button
                  type="button"
                  onClick={useMyLocation}
                  disabled={locating}
                  className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 af-mono text-[10px] uppercase tracking-[0.15em] text-zinc-300 hover:border-[#FF2800] hover:text-white disabled:opacity-50 transition-colors"
                  data-testid="use-my-location-button"
                >
                  <LocateFixed size={12} />
                  {locating ? "Locating…" : "Use my location"}
                </button>
              </div>
              {[
                { key: "name", label: "Full name", placeholder: "Riaan Vatsa" },
                { key: "phone", label: "Phone", placeholder: "98200 78697" },
                { key: "email", label: "Email", placeholder: "you@example.com", type: "email" },
                { key: "address", label: "Address (house / street / area)", placeholder: "Flat 4B, Apex Towers, Linking Road" },
                { key: "city", label: "City", placeholder: "Mumbai" },
                { key: "state", label: "State", placeholder: "Maharashtra" },
                { key: "pincode", label: "PIN code", placeholder: "400050" },
              ].map((f) => {
                const missing =
                  attempted &&
                  (!addr[f.key].trim() ||
                    (f.key === "pincode" && !/^\d{6}$/.test(addr.pincode.trim())));
                return (
                  <label key={f.key} className="block">
                    <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1.5">
                      {f.label}{" "}
                      <span
                        className={missing ? "text-[#FF2800]" : ""}
                        data-testid={missing ? `shipping-${f.key}-required` : undefined}
                      >
                        *
                      </span>
                    </span>
                    <input
                      type={f.type || "text"}
                      value={addr[f.key]}
                      onChange={setField(f.key)}
                      placeholder={f.placeholder}
                      className={`w-full bg-black border px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF2800] transition-colors placeholder:text-zinc-700 ${
                        missing ? "border-[#FF2800]/70" : "border-white/15"
                      }`}
                      data-testid={`shipping-${f.key}-input`}
                    />
                  </label>
                );
              })}
            </div>
          )}

          {step === "cart" &&
            items.map((it) => (
            <div
              key={it.cart_id}
              className="flex gap-4 p-3 border border-white/10 bg-[#141414]"
              data-testid={`cart-item-${it.cart_id}`}
            >
              <div
                className="w-20 h-20 bg-black border border-white/10 flex-shrink-0 overflow-hidden"
                style={{
                  backgroundImage: `url(${it.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="af-display font-semibold text-sm leading-tight">
                    {it.name}
                  </h4>
                  <button
                    type="button"
                    onClick={() => removeItem(it.cart_id)}
                    className="text-zinc-500 hover:text-[#FF2800] transition-colors"
                    aria-label="Remove"
                    data-testid={`remove-cart-item-${it.cart_id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {it.options && (
                  <p className="af-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1 leading-relaxed">
                    {describeOptions(it.options)}
                  </p>
                )}
                <p className="af-mono text-[11px] text-zinc-300 mt-1">
                  {formatINR(it.unit_price)}
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => updateQty(it.cart_id, Math.max(1, it.quantity - 1))}
                    className="w-7 h-7 border border-white/15 hover:border-white/40 inline-flex items-center justify-center"
                    data-testid={`decrease-qty-${it.cart_id}`}
                    aria-label="Decrease quantity"
                  >
                    <Minus size={12} />
                  </button>
                  <span className="af-mono text-xs w-6 text-center">
                    {it.quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => updateQty(it.cart_id, it.quantity + 1)}
                    className="w-7 h-7 border border-white/15 hover:border-white/40 inline-flex items-center justify-center"
                    data-testid={`increase-qty-${it.cart_id}`}
                    aria-label="Increase quantity"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/10 p-6 space-y-4">
            <div className="flex items-end justify-between">
              <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                // Subtotal
              </span>
              <span
                className="af-display text-2xl font-bold"
                data-testid="cart-subtotal"
              >
                {formatINR(total)}
              </span>
            </div>
            {total > 10000 ? (
              <>
                <div className="flex items-center justify-between -mt-2" data-testid="cart-deal-discount">
                  <span className="af-mono text-[10px] uppercase tracking-[0.2em] text-[#C6FF00]">
                    Deal: over ₹10,000
                  </span>
                  <span className="af-mono text-sm text-[#C6FF00]">−₹500</span>
                </div>
                <div className="flex items-center justify-between -mt-1">
                  <span className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    To pay
                  </span>
                  <span className="af-display text-xl font-bold" data-testid="cart-payable">
                    {formatINR(total - 500)}
                  </span>
                </div>
              </>
            ) : (
              total > 0 && (
                <p
                  className="af-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 -mt-2"
                  data-testid="cart-deal-hint"
                >
                  Spend over ₹10,000 — ₹500 off, applied automatically
                </p>
              )
            )}
            <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-right -mt-2" data-testid="cart-shipping-note">
              Shipping not included
            </p>
            {step === "cart" ? (
              <button
                type="button"
                onClick={() => setStep("address")}
                className="w-full bg-[#FF2800] text-white font-semibold uppercase tracking-wider py-4 hover:bg-[#CC2000] transition-colors text-sm"
                data-testid="checkout-button"
              >
                Proceed to checkout
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full bg-[#FF2800] text-white font-semibold uppercase tracking-wider py-4 hover:bg-[#CC2000] disabled:opacity-50 transition-colors text-sm"
                  data-testid="pay-now-button"
                >
                  {loading
                    ? "Opening secure payment…"
                    : `Pay ${formatINR(total > 10000 ? total - 500 : total)}`}
                </button>
                <button
                  type="button"
                  onClick={() => setStep("cart")}
                  className="w-full border border-white/15 text-zinc-300 font-semibold uppercase tracking-wider py-3 hover:border-white/40 transition-colors text-xs"
                  data-testid="back-to-cart-button"
                >
                  Back to cart
                </button>
              </>
            )}
            <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-center">
              Secure payment via Razorpay · UPI, Cards, Netbanking
            </p>
          </div>
        )}
      </aside>
    </div>
  );
}
